import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { StyledSubBreadcrumbs } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/styled';
import { Flex, Loader, Text } from '@mantine/core';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { UserDetails } from '@dealstack/frontend/shared/components/UserDetails/UserDetails';
import { UserSidebar } from '@dealstack/frontend/shared/components/UserSidebar/UserSidebar';
import { UserPermissions } from '@dealstack/frontend/shared/components/UserPermissions/UserPermissions';
import { useAppSelector } from '@dealstack/frontend/admin/hooks/redux';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { selectShouldDisableIsContactPerson } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.selectors';
import {
	PermissionRole,
	ProgrammeUserWithPermissionRolesRes,
} from '@dealstack/shared/lib/generated';
import { TransformedPermission } from '@dealstack/frontend/shared/components/UserPermissions/UserPermissions.types';

interface Props {
	title: string;
	deleteFromText: string;
	user?: Omit<
		ProgrammeUserWithPermissionRolesRes,
		'userProgrammeId' | 'permissionRoles' | 'isContactPerson' | 'canBeRemoved'
	> & {
		permissions: TransformedPermission[];
		isContactPerson?: boolean;
	};
	isLoading: boolean;
	isContactPerson?: boolean;
	showIsContactPerson?: boolean;
	membersBreadcrumbTitle: string;
	handleUpdateContactPerson?: (isContactPerson: boolean) => void;
	handleUpdateUser: () => Promise<void>;
	handleRemoveUser: (userId: string) => void;
	canRemoveUser?: boolean;
	handlePermissionChange: (permissionRole: PermissionRole, isEnabled: boolean) => void;
	text: {
		permissionTitle: string;
	};
}

export const UserManagement = ({
	title,
	deleteFromText,
	user,
	isLoading,
	isContactPerson = false,
	showIsContactPerson = false,
	membersBreadcrumbTitle,
	handleUpdateContactPerson,
	handleUpdateUser,
	handleRemoveUser,
	handlePermissionChange,
	canRemoveUser = true,
	text,
}: Props) => {
	const currentUserId = useAppSelector(selectUserId);
	const shouldDisableIsContactPerson = useAppSelector(selectShouldDisableIsContactPerson);

	if (isLoading) {
		return <Loader />;
	}

	if (!user) {
		return <Text variant="p-sm">User not found</Text>;
	}

	const phoneNumber = `${user.telephonePrefix ?? ''} ${user.phoneNumber ?? ''}`;

	return (
		<Flex direction="column" h="100%" w="100%" style={{ overflow: 'auto' }}>
			<ContainerWithHeader
				title={
					<StyledSubBreadcrumbs align="center" gap="0.5rem">
						<Text variant="p-sm">{title}</Text>
						<Icon variant="backslash" size="vlg" />
						<Text variant="p-sm">{membersBreadcrumbTitle}</Text>
						<Icon variant="backslash" size="vlg" />
						<Text variant="p-sm">
							{user.firstName} {user.lastName}
						</Text>
					</StyledSubBreadcrumbs>
				}
			>
				<Flex gap="1rem">
					<Flex direction="column" gap="1rem" w="100%">
						<UserDetails
							firstName={user.firstName}
							lastName={user.lastName}
							phoneNumber={phoneNumber}
							email={user.email}
							onUpdate={handleUpdateUser}
						/>
						<UserPermissions
							permissions={user.permissions}
							handlePermissionChange={handlePermissionChange}
							text={text}
						/>
					</Flex>
					<UserSidebar
						firstName={user.firstName}
						lastName={user.lastName}
						email={user.email}
						showIsContactPerson={showIsContactPerson}
						deleteFromText={deleteFromText}
						showRemoveUserButton={currentUserId !== user.id && canRemoveUser}
						onRemoveUserClicked={() => handleRemoveUser(user.id)}
						isContactPerson={isContactPerson}
						disableIsContactPerson={shouldDisableIsContactPerson && isContactPerson}
						onUpdateContactPersonClicked={(isContactPerson: boolean) =>
							handleUpdateContactPerson?.(isContactPerson)
						}
					/>
				</Flex>
			</ContainerWithHeader>
		</Flex>
	);
};
